import * as analytics from '@cartwheel/analytics'
import * as Turbo from '@hotwired/turbo'

// CSP nonce handling for Turbo Drive navigation
// Pass the current nonce with fetch requests so server can reuse it
document.addEventListener('turbo:before-fetch-request', (event: CustomEvent) => {
  const nonce = document.querySelector<HTMLMetaElement>("meta[name='csp-nonce']")?.content
  if (nonce) {
    event.detail.fetchOptions.headers['X-Turbo-Nonce'] = nonce
  }
})

// Preserve nonces on script tags before Turbo caches the page
document.addEventListener('turbo:before-cache', () => {
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[nonce]')) {
    script.setAttribute('nonce', script.nonce || '')
  }
})

const initializeAnalytics = async () => {
  await analytics.initialize()

  document.addEventListener('turbo:load', () => {
    analytics.page()
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAnalytics, { once: true })
} else {
  void initializeAnalytics()
}

// (Hopefully temporary) custom turbo_stream action that lets the server response decide to "break out"
// of any turbo-frame element.
//
// Usage in Rails controller:
//   render turbo_stream: turbo_stream.action(:redirect, <redirect path>)
//
// Looks like this is a pending feature in some fashion:
// https://github.com/hotwired/turbo-rails/pull/367#issuecomment-1934729149
Turbo.StreamActions['redirect'] = function () {
  Turbo.visit(this.target)
}

// Stimulus controllers
import { Application } from '@hotwired/stimulus'
import { registerControllers } from 'stimulus-vite-helpers'

const application = Application.start()

const controllers = import.meta.glob('/controllers/*_controller.js', { eager: true })
registerControllers(application, controllers)

window.Stimulus = application

// Import the Hotwire/Turbo-specific version of Flowbite
import 'flowbite/dist/flowbite.turbo.js'
// Flowbite turbo support seems to handle everything except when we return a 422 to show errors, so
// handle that manually here.
// Source: https://github.com/themesberg/flowbite/issues/88#issuecomment-1962238351
window.document.addEventListener('turbo:submit-end', (_event) => {
  window.setTimeout(() => {
    if (typeof window.initFlowbite === 'function') {
      window.initFlowbite()
    }
  }, 10)
})

import Chart from 'chart.js/auto'
// @ts-expect-error chartkick doesn't have a type library, what's the most idiomatic way to handle this?
import Chartkick from 'chartkick'

window.Chartkick = Chartkick
window.Chart = Chart

// Extend the Window interface to include global libraries
declare global {
  interface Window {
    Turbo?: {
      visit: (url: string) => void
    }
    Stimulus?: typeof application
    Chartkick?: typeof Chartkick
    Chart?: typeof Chart
    initFlowbite?: () => void
  }
}
